import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { RoutingConfigSummaryRecord, RoutingDrPlanRecord } from '@nadoutong/contracts';
import {
  useDeleteRoutingDrPlan,
  useRoutingDrPlans,
  useUpsertRoutingDrPlan,
} from '../../api/useRoutingDr.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  EmptyState,
  FormField,
  Input,
  Panel,
  Select,
  TBody,
  Table,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { PreflightReportDrawer } from './PreflightReportDrawer.tsx';
import { compareDrPlans, drPlanVerdict, drPlanVerificationAge } from './drPlanStatus.ts';
import type { DrPlanFreshness } from './drPlanStatus.ts';

/**
 * The DR plan list (#1421 P3b).
 *
 * The column this surface exists for is verification staleness. A plan
 * verified five weeks ago is the dangerous one — it reads as "verified" in
 * anybody's memory while the supply behind it has moved — so the age is
 * rendered as an explicit chip next to the timestamp instead of leaving the
 * operator to subtract two dates during an incident.
 */

interface PlanDraft {
  /** Empty while marking a config that is not a plan yet. */
  readonly config_id: string;
  readonly label: string;
  readonly notes: string;
  readonly plan_rank: string;
  readonly editing: boolean;
}

/**
 * A never-verified plan is not a calm state. `neutral` would render it quieter
 * than a plan verified eight days ago, which inverts the actual risk: an
 * `aging` plan at least passed something once, and this one has never been
 * shown to work at all. It sits at `bad` alongside `stale` — the labels, not
 * the tone, carry the difference between "too old to trust" and "never
 * checked", and both call for the same next action.
 */
const FRESHNESS_TONE: Record<DrPlanFreshness, 'ok' | 'warn' | 'bad' | 'neutral'> = {
  fresh: 'ok',
  aging: 'warn',
  stale: 'bad',
  never: 'bad',
  unreadable: 'bad',
};

export function RoutingDrPlansPanel({
  configs,
}: {
  readonly configs: readonly RoutingConfigSummaryRecord[];
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const plans = useRoutingDrPlans();
  const upsertPlan = useUpsertRoutingDrPlan();
  const deletePlan = useDeleteRoutingDrPlan();
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [unmarking, setUnmarking] = useState<RoutingDrPlanRecord | null>(null);
  const [preflightConfigId, setPreflightConfigId] = useState<string | null>(null);

  const rows = useMemo(
    () => [...(plans.data?.plans ?? [])].sort(compareDrPlans),
    [plans.data],
  );
  // `Date.now()` is read once per render rather than on a timer: the ages this
  // column reports are measured in days, and a ticking clock would re-render
  // the whole table for no operator-visible change.
  const nowMs = Date.now();
  const markedConfigIds = useMemo(
    () => new Set(rows.map((plan) => plan.config_id)),
    [rows],
  );
  const markableConfigs = useMemo(
    () => configs.filter((config) => !markedConfigIds.has(config.id)),
    [configs, markedConfigIds],
  );
  const preflightPlan = rows.find((plan) => plan.config_id === preflightConfigId) ?? null;
  const preflightLabel = preflightPlan?.label
    ?? configs.find((config) => config.id === preflightConfigId)?.name
    ?? preflightConfigId
    ?? '';

  async function handleDraftSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (draft === null) return;
    const configId = draft.config_id.trim();
    const label = draft.label.trim();
    if (configId.length === 0) {
      toast.error(t('routing_dr.plans.error.config_required'));
      return;
    }
    if (label.length === 0) {
      toast.error(t('routing_dr.plans.error.label_required'));
      return;
    }
    const rank = Number.parseInt(draft.plan_rank.trim(), 10);
    if (draft.plan_rank.trim().length > 0 && (!Number.isFinite(rank) || rank < 0)) {
      toast.error(t('routing_dr.plans.error.rank_invalid'));
      return;
    }
    try {
      await upsertPlan.mutateAsync({
        configId,
        label,
        notes: draft.notes.trim().length > 0 ? draft.notes.trim() : null,
        ...(draft.plan_rank.trim().length > 0 ? { plan_rank: rank } : {}),
      });
      toast.success(
        draft.editing ? t('routing_dr.plans.toast.updated') : t('routing_dr.plans.toast.marked'),
      );
      setDraft(null);
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  async function handleUnmark(): Promise<void> {
    if (unmarking === null) return;
    try {
      await deletePlan.mutateAsync({ configId: unmarking.config_id });
      toast.success(t('routing_dr.plans.toast.unmarked'));
      setUnmarking(null);
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  return (
    <>
      <Panel
        caption={t('routing_dr.plans.title')}
        count={rows.length}
        testId="routing-dr-plans-panel"
        actions={
          <Button
            size="sm"
            disabled={markableConfigs.length === 0}
            onClick={() => setDraft({
              config_id: '',
              label: '',
              notes: '',
              plan_rank: '',
              editing: false,
            })}
            data-testid="routing-dr-plan-mark"
          >
            {t('routing_dr.plans.mark')}
          </Button>
        }
      >
        {plans.isPending ? (
          <p className="m-0 p-4 text-body text-paper-low" data-testid="routing-dr-plans-loading">
            {t('routing_dr.plans.loading')}
          </p>
        ) : rows.length === 0 ? (
          <EmptyState
            title={t('routing_dr.plans.empty_title')}
            hint={t('routing_dr.plans.empty_hint')}
            testId="routing-dr-plans-empty"
            action={
              <Button
                size="sm"
                disabled={markableConfigs.length === 0}
                onClick={() => setDraft({
                  config_id: '',
                  label: '',
                  notes: '',
                  plan_rank: '',
                  editing: false,
                })}
                data-testid="routing-dr-plan-mark-empty"
              >
                {t('routing_dr.plans.mark')}
              </Button>
            }
          />
        ) : (
          <Table>
            <THead>
              <Tr interactive={false}>
                <Th>{t('routing_dr.plans.column.plan')}</Th>
                <Th num>{t('routing_dr.plans.column.rank')}</Th>
                <Th>{t('routing_dr.plans.column.config_status')}</Th>
                <Th>{t('routing_dr.plans.column.last_verified')}</Th>
                <Th>{t('routing_dr.plans.column.result')}</Th>
                <Th className="text-right">{t('tenant_routing.table.actions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((plan) => (
                <Tr key={plan.config_id} interactive={false} data-testid={`routing-dr-plan-row-${plan.config_id}`}>
                  <Td emphasis>
                    <span className="truncate">{plan.label}</span>
                    <span className="mt-0.5 block truncate font-mono text-micro text-paper-low">
                      {plan.config_name ?? plan.config_id}
                    </span>
                    {plan.notes === null || plan.notes.length === 0 ? null : (
                      <span className="mt-0.5 block truncate text-dense text-paper-low">
                        {plan.notes}
                      </span>
                    )}
                  </Td>
                  <Td num>{plan.plan_rank}</Td>
                  <Td>
                    <Chip tone={plan.config_status === 'active' ? 'ok' : 'bad'}>
                      {t(`tenant_routing.configs.status.${plan.config_status}`)}
                    </Chip>
                  </Td>
                  <Td>
                    <PlanFreshnessCell plan={plan} nowMs={nowMs} />
                  </Td>
                  <Td>
                    <PlanVerdictCell plan={plan} nowMs={nowMs} />
                  </Td>
                  <Td className="text-right">
                    <span className="inline-flex items-center gap-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPreflightConfigId(plan.config_id)}
                        data-testid={`routing-dr-plan-preflight-${plan.config_id}`}
                      >
                        {t('routing_dr.plans.action.preflight')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDraft({
                          config_id: plan.config_id,
                          label: plan.label,
                          notes: plan.notes ?? '',
                          plan_rank: String(plan.plan_rank),
                          editing: true,
                        })}
                        data-testid={`routing-dr-plan-edit-${plan.config_id}`}
                      >
                        {t('routing_dr.plans.action.edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setUnmarking(plan)}
                        data-testid={`routing-dr-plan-unmark-${plan.config_id}`}
                      >
                        {t('routing_dr.plans.action.unmark')}
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>

      <Dialog
        open={draft !== null}
        onOpenChange={(open) => {
          if (!open) setDraft(null);
        }}
        title={draft?.editing === true
          ? t('routing_dr.plans.edit_title')
          : t('routing_dr.plans.mark_title')}
        closeLabel={t('common.close')}
        testId="routing-dr-plan-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)} disabled={upsertPlan.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="routing-dr-plan-form"
              loading={upsertPlan.isPending}
              data-testid="routing-dr-plan-save"
            >
              {draft?.editing === true
                ? t('routing_dr.plans.save_edit')
                : t('routing_dr.plans.save_mark')}
            </Button>
          </>
        }
      >
        {draft === null ? null : (
          <form
            id="routing-dr-plan-form"
            className="flex flex-col gap-4"
            onSubmit={handleDraftSubmit}
            data-testid="routing-dr-plan-form"
          >
            {draft.editing ? (
              <div className="flex items-center justify-between gap-3 rounded-md bg-well px-3 py-2 text-dense text-paper-low">
                <span>{t('routing_dr.plans.column.config')}</span>
                <span className="font-mono text-paper-mid">{draft.config_id}</span>
              </div>
            ) : (
              <FormField
                label={t('routing_dr.plans.column.config')}
                fieldId="routing-dr-plan-config"
                required
                hint={t('routing_dr.plans.config_hint')}
              >
                <Select
                  id="routing-dr-plan-config"
                  value={draft.config_id.length > 0 ? draft.config_id : undefined}
                  onValueChange={(configId) => {
                    setDraft((current) => current === null ? current : { ...current, config_id: configId });
                  }}
                  options={markableConfigs.map((config) => ({
                    value: config.id,
                    label: config.name,
                  }))}
                  testId="routing-dr-plan-config"
                />
              </FormField>
            )}
            <FormField label={t('routing_dr.plans.label')} fieldId="routing-dr-plan-label" required>
              <Input
                id="routing-dr-plan-label"
                value={draft.label}
                onChange={(event) => {
                  const label = event.currentTarget.value;
                  setDraft((current) => current === null ? current : { ...current, label });
                }}
                placeholder={t('routing_dr.plans.label_placeholder')}
                data-testid="routing-dr-plan-label"
              />
            </FormField>
            <FormField
              label={t('routing_dr.plans.rank')}
              fieldId="routing-dr-plan-rank"
              hint={t('routing_dr.plans.rank_hint')}
            >
              <Input
                id="routing-dr-plan-rank"
                mono
                inputMode="numeric"
                value={draft.plan_rank}
                onChange={(event) => {
                  const planRank = event.currentTarget.value;
                  setDraft((current) => current === null ? current : { ...current, plan_rank: planRank });
                }}
                data-testid="routing-dr-plan-rank"
              />
            </FormField>
            <FormField
              label={t('routing_dr.plans.notes')}
              fieldId="routing-dr-plan-notes"
              hint={t('routing_dr.plans.notes_hint')}
            >
              <Textarea
                id="routing-dr-plan-notes"
                rows={3}
                value={draft.notes}
                onChange={(event) => {
                  const notes = event.currentTarget.value;
                  setDraft((current) => current === null ? current : { ...current, notes });
                }}
                data-testid="routing-dr-plan-notes"
              />
            </FormField>
          </form>
        )}
      </Dialog>

      <ConfirmDialog
        open={unmarking !== null}
        onOpenChange={(open) => {
          if (!open) setUnmarking(null);
        }}
        title={t('routing_dr.plans.unmark_title')}
        body={t('routing_dr.plans.unmark_body', { label: unmarking?.label ?? '' })}
        confirmLabel={t('routing_dr.plans.action.unmark')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={deletePlan.isPending}
        onConfirm={() => void handleUnmark()}
        testId="routing-dr-plan-unmark-dialog"
      />

      <PreflightReportDrawer
        open={preflightConfigId !== null}
        onClose={() => setPreflightConfigId(null)}
        configId={preflightConfigId}
        configLabel={preflightLabel}
        plan={preflightPlan}
      />
    </>
  );
}

function PlanFreshnessCell({
  plan,
  nowMs,
}: {
  readonly plan: RoutingDrPlanRecord;
  readonly nowMs: number;
}): JSX.Element {
  const t = useT();
  const age = drPlanVerificationAge(plan.last_verified_at, nowMs);
  if (age.freshness === 'never' || age.freshness === 'unreadable') {
    return (
      <Chip
        tone={FRESHNESS_TONE[age.freshness]}
        testId={`routing-dr-plan-freshness-${plan.config_id}`}
      >
        {age.freshness === 'never'
          ? t('routing_dr.plans.never_verified')
          : t('routing_dr.plans.unreadable_verified_at')}
      </Chip>
    );
  }
  const days = age.days ?? 0;
  const hours = age.hours ?? 0;
  const ago = days > 0
    ? t(days === 1 ? 'routing_dr.age.days_one' : 'routing_dr.age.days_many', { count: days })
    : hours > 0
      ? t(hours === 1 ? 'routing_dr.age.hours_one' : 'routing_dr.age.hours_many', { count: hours })
      : t('routing_dr.age.just_now');
  return (
    <span className="flex flex-col gap-0.5">
      <Chip
        tone={FRESHNESS_TONE[age.freshness]}
        testId={`routing-dr-plan-freshness-${plan.config_id}`}
      >
        {ago}
      </Chip>
      <span className="font-mono text-micro text-paper-low">
        {formatLocalTimestamp(plan.last_verified_at)}
      </span>
    </span>
  );
}

function PlanVerdictCell({
  plan,
  nowMs,
}: {
  readonly plan: RoutingDrPlanRecord;
  readonly nowMs: number;
}): JSX.Element {
  const t = useT();
  const result = plan.last_verify_result;
  if (result === null) {
    return <span className="text-dense text-paper-low">—</span>;
  }
  // A verdict with no readable date behind it is not a verdict an operator can
  // act on: "Passed" beside "Verification time unreadable" reads as a fresh
  // pass. The stored result is not discarded, it is labelled as undatable.
  if (drPlanVerificationAge(plan.last_verified_at, nowMs).freshness === 'unreadable') {
    return (
      <span
        className="text-dense text-paper-low"
        data-testid={`routing-dr-plan-verdict-undated-${plan.config_id}`}
      >
        {t('routing_dr.plans.verdict_undated')}
      </span>
    );
  }
  const verdict = drPlanVerdict(result.summary);
  return (
    <span className="flex flex-col gap-0.5">
      <Chip
        tone={verdict === 'clean' ? 'ok' : verdict === 'caution' ? 'warn' : 'bad'}
        testId={`routing-dr-plan-verdict-${plan.config_id}`}
      >
        {t(`routing_dr.verdict.${verdict}`)}
      </Chip>
      <span className="font-mono text-micro text-paper-low">
        {t('routing_dr.plans.verdict_counts', {
          uncovered: result.summary.uncovered,
          unhealthy: result.summary.unhealthy_targets,
          unreachable: result.summary.unreachable_orgs_total,
        })}
      </span>
    </span>
  );
}
